<template>
  <div>
    <div class="specification-wrapper">
      <div class="d-flex align-items-center mb-3" style="gap: 0.5rem">
        <div>
          <img src="~@/assets/icon/chip-blue.svg" alt="verified" width="24" />
        </div>
        <h5 class="section-title mb-0">Performance</h5>
      </div>
      <div class="d-flex" style="gap: 1rem">
        <div class="w-100">
          <input-text
            id="processor"
            label="Processor"
            :value="forms.productDetail.processor"
            placeholder="e.g. Intel Core i7-12700H"
            @change="
              (val) =>
                $emit('handleChangeInput', 'productDetail.processor', val)
            "
            :error="
              (errors &&
                errors.productDetail &&
                errors.productDetail.processor) ||
              null
            "
            :disabled="!!catalogId"
          />
        </div>
        <div class="w-100">
          <input-text
            id="graphics"
            label="Graphics Card"
            :value="forms.productDetail.vga"
            placeholder="e.g. NVIDIA GTX 1650"
            @change="
              (val) => $emit('handleChangeInput', 'productDetail.vga', val)
            "
            :error="
              (errors && errors.productDetail && errors.productDetail.vga) ||
              null
            "
            :disabled="!!catalogId"
          />
        </div>
      </div>
    </div>
    <br />
    <div class="specification-wrapper">
      <div class="d-flex align-items-center mb-3" style="gap: 0.5rem">
        <div>
          <img
            src="~@/assets/icon/memory-green.svg"
            alt="verified"
            width="24"
          />
        </div>
        <h5 class="section-title mb-0">Memory</h5>
      </div>
      <div class="d-flex" style="gap: 1rem">
        <div class="w-100">
          <input-text
            id="ram"
            label="RAM"
            :value="forms.productDetail.ram"
            placeholder="8"
            showSuffixLabel
            @change="
              (val) => $emit('handleChangeInput', 'productDetail.ram', val)
            "
            :disabled="!!catalogId"
          >
            <template #suffix-label>GB</template>
          </input-text>
        </div>
        <div class="w-100">
          <input-radio
            label="RAM - Primary Slot Type"
            name="ram-slot-type"
            :value="forms.ramType"
            @change="(val) => $emit('handleChangeInput', 'ramType', val)"
            type="horizontal"
            :options="memoryTypeOptions"
          />
        </div>
      </div>
      <small class="text-danger m-0">{{
        (errors &&
          errors.productDetail &&
          (errors.productDetail.ram || errors.ramType)) ||
        ""
      }}</small>
      <div class="d-flex mt-3" style="gap: 1rem">
        <div class="w-100">
          <div class="d-flex" style="gap: 1rem">
            <div class="w-100">
              <single-select
                label="Storage"
                placeholder="Select Storage"
                id="storage"
                :value="
                  forms.productDetail.storage.length > 0
                    ? forms.productDetail.storage[0].type
                    : ''
                "
                :options="[
                  { label: 'SSD', value: 'SSD' },
                  { label: 'HDD', value: 'HDD' },
                  { label: 'SSD+HDD', value: 'SSD+HDD' },
                ]"
                :customClass="{ inputClass: !!catalogId ? '' : 'bg-white' }"
                @change="(val) => $emit('handleChangeStorage', val)"
                :disabled="!!catalogId"
              >
                <template #side-icon>
                  <i class="fa fa-chevron-down"></i>
                </template>
              </single-select>
            </div>
            <div class="w-100">
              <input-text
                id="storage-capacity"
                :value="
                  forms.productDetail.storage.length > 0 &&
                  forms.productDetail.storage[0].capacity
                    ? forms.productDetail.storage[0].capacity
                    : ''
                "
                @change="(val) => $emit('handleChangeStorageCapacity', val, 0)"
                placeholder="8"
                showSuffixLabel
                :disabled="!!catalogId"
                :parser="(val) => val.replace(/[^0-9]/g, '')"
              >
                <template #suffix-label>GB</template>
              </input-text>
            </div>
          </div>
          <div
            v-if="forms.productDetail.storage.length > 1"
            class="w-100 d-flex mt-3"
            style="gap: 1rem"
          >
            <div class="w-100">
              <input-text id="hdd" value="HDD" disabled />
            </div>
            <div class="w-100">
              <input-text
                id="storage-capacity-2"
                :value="
                  forms.productDetail.storage.length > 0 &&
                  forms.productDetail.storage[1].capacity
                    ? forms.productDetail.storage[1].capacity
                    : ''
                "
                @change="(val) => $emit('handleChangeStorageCapacity', val, 1)"
                placeholder="8"
                showSuffixLabel
                :disabled="!!catalogId"
                :parser="(val) => val.replace(/[^0-9]/g, '')"
              >
                <template #suffix-label>GB</template>
              </input-text>
            </div>
          </div>
        </div>
        <div class="w-100">
          <input-radio
            label="Storage - Primary Slot Type"
            name="storage-slot-type"
            :value="forms.storageType"
            @change="(val) => $emit('handleChangeInput', 'storageType', val)"
            type="horizontal"
            :options="memoryTypeOptions"
          />
        </div>
      </div>
      <small class="text-danger m-0">{{ errorStorage }}</small>
    </div>
    <br />
    <div class="specification-wrapper">
      <div class="d-flex align-items-center mb-3" style="gap: 0.5rem">
        <div>
          <img
            src="~@/assets/icon/desktop-purple.svg"
            alt="verified"
            width="24"
          />
        </div>
        <h5 class="section-title mb-0">Display</h5>
      </div>
      <div class="d-flex" style="gap: 1rem">
        <div class="w-50">
          <input-text
            id="screen"
            label="Screen"
            :value="forms.productDetail.screen"
            placeholder="e.g. 15.6"
            showSuffixLabel
            @change="
              (val) => $emit('handleChangeInput', 'productDetail.screen', val)
            "
            :error="
              (errors && errors.productDetail && errors.productDetail.screen) ||
              null
            "
            :disabled="!!catalogId"
          >
            <template #suffix-label>inch</template>
          </input-text>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { error } from "shelljs";
import {
  SingleSelect,
  InputText,
  InputTextarea,
  InputRadio,
} from "../../../Input";

export default {
  name: "LaptopAndCpu",
  components: {
    SingleSelect,
    InputText,
    InputTextarea,
    InputRadio,
  },
  props: {
    errors: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    forms: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      memoryTypeOptions: [
        { label: "Removable", value: "removable" },
        { label: "Onboard", value: "onboard" },
      ],
    };
  },
  computed: {
    catalogId() {
      const id = Number(this.$route.params.id);
      return isNaN(id) ? 0 : id;
    },
    errorStorage() {
      if (this.errors) {
        if (this.errors.storageType) {
          return this.errors.storageType;
        }
        if (this.errors.productDetail && this.errors.productDetail.storage) {
          for (let i = 0; i < this.errors.productDetail.storage.length; i++) {
            if (this.errors.productDetail.storage[i].type) {
              return this.errors.productDetail.storage[i].type;
            }
            if (this.errors.productDetail.storage[i].capacity) {
              return this.errors.productDetail.storage[i].capacity;
            }
          }
        }
      }

      return "";
    },
  },
};
</script>

<style scoped>
.section-title {
  font-weight: 700;
  font-size: 1.125rem;
  margin-bottom: 0.5rem;
}

.section-description {
  font-weight: 400;
  font-size: 0.875rem;
}

.specification-wrapper {
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 0.5rem;
}

.bg-white {
  background: #ffffff;
}
</style>


// WEBPACK FOOTER //
// src/components/Catalog/Components/TechSpec/LaptopAndCpu.vue